import React from 'react';
import { styled } from '@mui/material/styles';
import ListItemButton, {
  ListItemButtonProps,
} from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { NavItem } from '../../nav';
import { renderSideNavItemIcon } from './renderSideNavItemIcon';
import { Theme } from '@mui/system';

type Props = {
  active: boolean;
  'data-testid'?: string;
  hasItems: boolean;
  hidden?: boolean;
  hoveredItem: NavItem | null;
  isOpen: boolean;
  isPrimary?: boolean;
  navItem: NavItem;
  setHoveredItem: (hoveredItem: NavItem | null) => void;
} & ListItemButtonProps;

export function SideNavButton(props: Props) {
  const {
    active,
    children,
    'data-testid': testid = 'required',
    hasItems,
    hidden,
    hoveredItem,
    isOpen,
    isPrimary = false,
    navItem,
    setHoveredItem,
    ...others
  } = props;

  return (
    <StyledListItemButton
      aria-expanded={isOpen ? 'true' : undefined}
      aria-haspopup="true"
      aria-label={typeof children === 'string' ? children : 'menu-item'}
      className={active ? 'active' : undefined}
      data-testid={testid}
      disableRipple
      hidden={hidden}
      isHover={hoveredItem?.key === navItem.key}
      isPrimary={isPrimary}
      isVisible={!!hoveredItem?.key}
      onMouseEnter={() => setHoveredItem(navItem)}
      onMouseLeave={() => {
        if (!isOpen) {
          setHoveredItem(null);
        }
      }}
      onMouseOver={() => setHoveredItem(navItem)}
      {...others}
    >
      <StyledListItemIcon isPrimary={isPrimary}>
        {renderSideNavItemIcon(
          navItem.key,
          active,
          hoveredItem?.key === navItem.key
        )}
      </StyledListItemIcon>
      <StyledTypography
        isHover={hoveredItem?.key === navItem.key}
        isVisible={!!hoveredItem?.key}
      >
        {children}
      </StyledTypography>
      {hasItems && (
        <StyledArrowIcon isHover={false} isVisible={!!hoveredItem?.key}>
          <ArrowForwardIosIcon
            sx={{
              height: '12px',
              minWidth: 'inherit',
              width: '12px',
            }}
          />
        </StyledArrowIcon>
      )}
    </StyledListItemButton>
  );
}

interface AvailabilityProps {
  isHover?: boolean;
  isPrimary?: boolean;
  isVisible?: boolean;
}

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (props) => props !== 'isPrimary',
})<AvailabilityProps>(({ isPrimary, theme }) => ({
  color: theme.palette.grey[isPrimary ? 600 : 500],
  marginRight: isPrimary ? 8 : 0,
  minWidth: 24,
}));

const getWidthStyledListItemButton = (
  isPrimary: boolean | undefined,
  isVisible: boolean | undefined
) => (!isPrimary && !isVisible ? '40px' : '240px');

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (props) =>
    props !== 'isPrimary' && props !== 'isHover' && props !== 'isVisible',
})<AvailabilityProps>(({ hidden, isPrimary, isVisible, theme }) => ({
  '&.active': {
    '& .MuiTypography-root': {
      color: isPrimary
        ? theme.palette.primary[900]
        : theme.palette.primary.main,
    },
    '&:hover': {
      '& .MuiTypography-root': {
        color: isPrimary
          ? theme.palette.primary[900]
          : theme.palette.primary.main,
      },
    },
    backgroundColor: isPrimary
      ? theme.palette.primary[50]
      : theme.palette.common.white,
  },
  '&:first-of-type': {
    marginTop: isPrimary ? 0 : 16,
  },
  '&:hover': {
    '& .MuiTypography-root': {
      color: isPrimary
        ? theme.palette.primary[900]
        : theme.palette.primary.main,
    },
  },
  '&:not(&:first-of-type)': {
    marginTop: isPrimary ? 8 : 16,
  },
  alignItems: 'center',
  borderRadius: 4,
  display: hidden ? 'none' : 'flex',
  flexDirection: 'row',
  fontSize: '14px',
  justifyContent: isPrimary ? 'left' : 'center',
  maxHeight: isPrimary ? 48 : 40,
  overflow: 'hidden',
  padding: isPrimary ? '0 13px' : '0 10px',
  textAlign: 'center',
  width: isPrimary
    ? '100%'
    : getWidthStyledListItemButton(isPrimary, isVisible),
}));

const getColorStyledTypography = (
  isHover: boolean | undefined,
  isPrimary: boolean | undefined,
  theme: Theme
) => {
  if (!isPrimary && isHover) return theme.palette.grey[700];
  else if (isHover) return theme.palette.primary.main;
  else return theme.palette.grey[600];
};

const StyledTypography = styled(Typography, {
  shouldForwardProp: (props) =>
    props !== 'isPrimary' && props !== 'isHover' && props !== 'isVisible',
})<AvailabilityProps>(({ isHover, isPrimary, isVisible, theme }) => ({
  color: getColorStyledTypography(isHover, isPrimary, theme),
  display: isVisible ? 'block' : 'none',
  fontSize: '14px',
  marginLeft: isPrimary ? 0 : 10,
  textAlign: 'left',
  width: '100%',
}));

const StyledArrowIcon = styled(ListItemIcon, {
  shouldForwardProp: (props) => props !== 'isVisible' && props !== 'isHover',
})<AvailabilityProps>(({ isVisible, theme }) => ({
  display: isVisible ? 'block' : 'none',
  marginRight: '-5px',
  minWidth: 'inherit',
}));
